import { ChangeEvent, useState, useEffect } from "react";
import { GetFuelRatesQueryOptions } from "src/api/rate-calculator-query-options";
import { useQuery } from "@tanstack/react-query";
import { FormField } from "src/components/formfield";
import { GetLoadedMiles } from "src/api/rate-calculator.api";
import { useRateCalculator } from "../context/rate-calculator-state";
import useAccountEnhanced from "src/hooks/use-account-enhanced";
import {
    PostFormBidRates,
    PostFormRateHistory,
    GetLocations,
} from "src/api/rate-calculator.api";

import { ManualMiles } from "./manual-miles";
import { Modal } from "src/components/modal";
import { SearchSelect, SelectOption } from "./search-select";

export function CalculatorForm() {
    const accountEnhanced = useAccountEnhanced();
    const UPN = accountEnhanced.account.username;

    //State Variables
    const rateCalculatorState = useRateCalculator();

    // Get fuel on page load
    const fuelRatesQuery = useQuery(GetFuelRatesQueryOptions());
    const fuelFromAPI = fuelRatesQuery.data;
    const fuelSurcharge = fuelFromAPI?.[0]?.fuelSurcharge;

    type formError = {
        currentFuel: string;
        loadedMiles: string;
        emptyToOrigin: string;
        emptyAfterDelivery: string;
        origin: string;
        destination: string;
    };

    const formDefault = {
        currentFuel: "",
        loadedMiles: "",
        emptyToOrigin: "",
        emptyAfterDelivery: "",
        totalEstimatedMiles: "",
        origin: "",
        destination: "",
    };

    const [formData, setFormData] = useState(formDefault);
    const [errors, setErrors] = useState<formError>();
    const [loadingMiles, setLoadingMiles] = useState<boolean | undefined>();
    const [loadedMiles, setLoadedMiles] = useState<number | undefined>();
    const [manualMiles, setManualMiles] = useState<boolean>(false);

    const clearResults = () => {
        setManualMiles(false);
        setLoadedMiles(undefined);
        rateCalculatorState.clearCurrentRateData();
        rateCalculatorState.clearBidRates();
    };

    type Location = {
        city: string;
        state: string;
        zip: string;
    };

    const locationDefault = {
        city: "",
        state: "",
        zip: "",
    };

    const [origin, setOrigin] = useState<Location>(locationDefault);
    const [destination, setDestination] = useState<Location>(locationDefault);
    function parseLocation(locationString: string) {
        const regex = /^([\w ]+), (\w{2}) (\d{5})$/;
        var found = locationString.trim().match(regex);

        return {
            city: found?.[1],
            state: found?.[2],
            zip:
                found?.[3] ??
                locationString.substring(locationString.length - 5), // Fallback to zip
        } as Location;
    }

    useEffect(() => {
        setOrigin(parseLocation(formData.origin));
        setDestination(parseLocation(formData.destination));
    }, [formData.origin, formData.destination]);

    useEffect(() => {
        if (isValidZip(origin.zip) && isValidZip(destination.zip)) {
            fetchLoadedMiles(
                `${origin.city},${origin.state},${origin.zip}`,
                `${destination.city},${destination.state},${destination.zip}`,
            );
        }
    }, [origin, destination]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();

        const { name, value } = event.target;
        setFormData((prevState) => ({ ...prevState, [name]: value }));
        setTotalMiles();
    };

    const isPositiveNumber = (value: string) => {
        return !isNaN(+value) && parseFloat(value) >= 0;
    };

    const isValidZip = (value: string) => {
        return /^\d{5}$/.test(value);
    };

    const validateForm = () => {
        var formIsValid = true;
        const errors: formError = {
            currentFuel: "",
            loadedMiles: "",
            emptyToOrigin: "",
            emptyAfterDelivery: "",
            origin: "",
            destination: "",
        };

        const positiveNumberMsg = "value must be a positive number";
        const zipMsg = "value must be 5 digit zip";
        if (!isPositiveNumber(formData.currentFuel))
            errors.currentFuel = positiveNumberMsg;

        if (!isPositiveNumber(formData.currentFuel))
            errors.currentFuel = positiveNumberMsg;

        if (!isPositiveNumber(formData.loadedMiles))
            errors.loadedMiles = positiveNumberMsg;

        if (!isPositiveNumber(formData.emptyToOrigin))
            errors.emptyToOrigin = positiveNumberMsg;

        if (!isPositiveNumber(formData.emptyAfterDelivery))
            errors.emptyAfterDelivery = positiveNumberMsg;

        if (!isValidZip(origin.zip)) errors.origin = zipMsg;

        if (!isValidZip(destination.zip)) errors.destination = zipMsg;

        setErrors(errors);

        Object.values(errors).forEach((value) => {
            if (value) {
                formIsValid = false;
            }
        });

        return formIsValid;
    };

    const getModal = (id: string) => {
        return document.getElementById(id) as HTMLDialogElement;
    };

    const showModal = (id: string) => {
        getModal(id).showModal();
    };

    const closeModal = (id: string) => {
        getModal(id).close();
    };

    const fetchLoadedMiles = async (origin: string, destination: string) => {
        setLoadingMiles(true);
        try {
            const result = await GetLoadedMiles(origin, destination);

            if (result !== null) {
                setManualMiles(false);
                formData.loadedMiles = "";
                setLoadedMiles(Math.round(result.miles));
                setFormData((prevState) => ({
                    ...prevState,
                    ["origin"]: `${result.originCity}, ${result.originState} ${result.originZipCode}`,
                }));
                setFormData((prevState) => ({
                    ...prevState,
                    ["destination"]: `${result.destinationCity}, ${result.destinationState} ${result.destinationZipCode}`,
                }));
            } else {
                // No miles found, enable manual entry.
                showModal("manualMilesModal");
            }
        } catch (_) {
        } finally {
            setLoadingMiles(false);
        }
    };

    const handleManualMilesCancel = () => {
        closeModal("manualMilesModal");
        setManualMiles(false);
    };

    const handleManualMilesSubmit = (miles: string) => {
        setFormData((prevState) => ({ ...prevState, ["loadedMiles"]: miles }));
        setManualMiles(true);
        closeModal("manualMilesModal");
    };

    const setTotalMiles = () => {
        var total = 0;
        total =
            parseInt(formData.loadedMiles) +
            parseInt(formData.emptyToOrigin) +
            parseInt(formData.emptyAfterDelivery);
        if (isNaN(total)) {
            return "0";
        }
        return total.toString();
    };

    const handleSubmit = async (event: ChangeEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!validateForm()) return; // Invalid form, bail early.

        rateCalculatorState.setLoading(true);
        rateCalculatorState.setLoadingHistory(true);

        try {
            var now = new Date();
            const avrlForm = {
                LoadId: `${origin.zip}-${destination.zip}`,
                PickupTime: now,
                DeliveryTime: null,
                EquipmentType: "dryvan",
                Miles: parseFloat(formData.totalEstimatedMiles),

                OriginCity: origin.city,
                OriginState: origin.state,
                OriginCountry: "USA",
                OriginZip: origin.zip,

                DestinationCity: destination.city,
                DestinationState: destination.state,
                DestinationCountry: "USA",
                DestinationZip: destination.zip,

                UPN: UPN,
                MilesFromAPI: !manualMiles,
            };

            const parsedData = {
                CurrentFuel: parseFloat(formData.currentFuel),
                LoadedMiles: parseFloat(formData.loadedMiles),
                EmptyToOrigin: parseFloat(formData.emptyToOrigin),
                EmptyAfterDelivery: parseFloat(formData.emptyAfterDelivery),
                TotalEstimatedMiles: parseFloat(formData.totalEstimatedMiles),
                OriginZip: origin.zip,
                DestinationZip: destination.zip,
                UPN: UPN,
                MilesFromAPI: !manualMiles,
            };
            rateCalculatorState.setCurrentFormData(avrlForm);

            var avrlBid = await PostFormBidRates(avrlForm);
            rateCalculatorState.setBidRates(avrlBid.data);
            rateCalculatorState.setBidCalc(avrlBid.breakdown);
            rateCalculatorState.setLoading(false);

            if (avrlBid?.data?.rate != 0) {
                var rateHistory = await PostFormRateHistory(parsedData);
                rateCalculatorState.setCurrentRateData(rateHistory.rateData);
            } else {
                rateCalculatorState.clearCurrentRateData();
            }
        } catch (error) {
            showModal("bidErrorModal");
        } finally {
            rateCalculatorState.setLoading(false);
            rateCalculatorState.setLoadingHistory(false);
        }
    };

    const [searchKey, setSearchKey] = useState<string>(Date.now().toString());
    const handleReset = () => {
        setFormData(formDefault);
        setSearchKey(Date.now().toString());
        clearResults();
    };

    const setSelectedOrigin = (selection: string) => {
        setSelectedLocation(selection, "origin");
    };

    const setSelectedDestination = (selection: string) => {
        setSelectedLocation(selection, "destination");
    };

    function setSelectedLocation(selection: string, context: string) {
        setFormData((prevState) => ({
            ...prevState,
            [context]: selection,
        }));
    }

    const getLocations = async (search: string) => {
        var locations = await GetLocations(search);
        var options = locations.map((loc) => {
            return {
                value: `${loc.city}, ${loc.state} ${loc.zipCode}`,
                label: `${loc.city}, ${loc.state} ${loc.zipCode}`,
            } as SelectOption;
        });
        return options;
    };

    return (
        <>
            <form onSubmit={handleSubmit}>
                <div className="flex flex-col">
                    <div className="flex flex-col" key={searchKey}>
                        <SearchSelect
                            title="Origin"
                            placeholder="City, State Zip"
                            value={formData.origin}
                            onSearch={getLocations}
                            onChange={setSelectedOrigin}
                            error={errors?.origin}
                        />

                        <SearchSelect
                            title="Destination"
                            placeholder="City, State Zip"
                            value={formData.destination}
                            onSearch={getLocations}
                            onChange={setSelectedDestination}
                            error={errors?.destination}
                        />
                    </div>

                    <fieldset className="fieldset mb-0">
                        <legend className="fieldset-legend text-xs font-medium text-gray-600">
                            MVT Fuel
                        </legend>
                        <div className="relative">
                            {fuelFromAPI ? (
                                ""
                            ) : (
                                <div className="absolute z-40 w-full flex jusitfy-center">
                                    <span className="loading loading-bars loading-xs mt-2 ml-4"></span>
                                </div>
                            )}
                            <input
                                className={
                                    "input input-bordered input-sm w-full pl-3 pr-8 " +
                                    "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none " +
                                    (errors?.emptyAfterDelivery &&
                                    errors?.emptyAfterDelivery.length > 0
                                        ? "ring-2 ring-red-500"
                                        : "")
                                }
                                type="number"
                                placeholder=""
                                name="currentFuel"
                                value={
                                    !formData.currentFuel && fuelSurcharge
                                        ? (formData.currentFuel =
                                              fuelSurcharge.toString())
                                        : formData.currentFuel
                                }
                                onChange={handleChange}
                                disabled={!fuelFromAPI}
                            />
                        </div>
                    </fieldset>

                    <fieldset className="fieldset mb-0">
                        <legend className="fieldset-legend text-xs font-medium text-gray-600">
                            Loaded Miles
                        </legend>
                        <div className="relative">
                            {loadingMiles ? (
                                <div className="absolute z-40 w-full flex jusitfy-center">
                                    <span className="loading loading-bars loading-xs mt-2 ml-4"></span>
                                </div>
                            ) : (
                                ""
                            )}
                            <input
                                className={
                                    "input input-bordered input-sm w-full pl-3 pr-8 " +
                                    "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none " +
                                    (errors?.emptyAfterDelivery &&
                                    errors?.emptyAfterDelivery.length > 0
                                        ? "ring-2 ring-red-500"
                                        : "")
                                }
                                type="number"
                                placeholder=""
                                name="loadedMiles"
                                value={
                                    !formData.loadedMiles && loadedMiles
                                        ? (formData.loadedMiles =
                                              loadedMiles.toString())
                                        : formData.loadedMiles
                                }
                                disabled={!manualMiles}
                                onChange={handleChange}
                            />
                        </div>
                        <p className="text-red-600 text-xs">
                            {errors?.loadedMiles}
                        </p>
                    </fieldset>

                    <FormField
                        className={
                            "input input-bordered input-sm w-full pl-3 pr-8 " +
                            "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none " +
                            (errors?.emptyToOrigin &&
                            errors?.emptyToOrigin.length > 0
                                ? "ring-2 ring-red-500"
                                : "")
                        }
                        legend="Empty to Origin"
                        type="number"
                        placeholder=""
                        name="emptyToOrigin"
                        value={formData.emptyToOrigin}
                        error={errors?.emptyToOrigin}
                        onChange={handleChange}
                        required
                    />

                    <FormField
                        className={
                            "input input-bordered input-sm w-full pl-3 pr-8 " +
                            "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none " +
                            (errors?.emptyAfterDelivery &&
                            errors?.emptyAfterDelivery.length > 0
                                ? "ring-2 ring-red-500"
                                : "")
                        }
                        legend="Empty after Delivery"
                        type="number"
                        placeholder=""
                        name="emptyAfterDelivery"
                        value={formData.emptyAfterDelivery}
                        error={errors?.emptyAfterDelivery}
                        onChange={handleChange}
                        required
                    />

                    <fieldset className="flex flex-row">
                        <legend className="fieldset-legend text-xs font-medium text-gray-600">
                            Total Miles:
                        </legend>
                        <span className="text-2xl text-nowrap">
                            {(formData.totalEstimatedMiles = setTotalMiles())}
                        </span>
                    </fieldset>

                    <button
                        type="submit"
                        className="btn btn-primary btn-sm mt-3"
                        disabled={loadingMiles}
                    >
                        {rateCalculatorState.loading ? (
                            <span className="loading loading-bars loading-xs"></span>
                        ) : (
                            "Get Rates"
                        )}
                    </button>

                    <button
                        type="reset"
                        className="btn btn-link btn-sm mt-3"
                        onClick={() => handleReset()}
                    >
                        Reset
                    </button>
                </div>
            </form>

            <dialog id="bidErrorModal" className="modal">
                <div className="modal-box">
                    <h1>NO RATE DATA</h1>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>

            <Modal id="manualMilesModal" style="max-w-2xl">
                <ManualMiles
                    formData={{
                        origin: origin.zip,
                        destination: destination.zip,
                    }}
                    handleCancel={handleManualMilesCancel}
                    handleSubmit={handleManualMilesSubmit}
                />
            </Modal>
        </>
    );
}
